// Utility functions for alerts, data fetching, and conversions
import { toast } from "react-toastify";

export const showAlert = (state, msg, timeout, delay = 0) => {
  const message = msg;

  // Added support for success
  if (state === "success") {
    toast.success(message, { autoClose: timeout });
  } else if (state === "warn") {
    toast.warning(message, { autoClose: timeout });
  } else if (state === "err") {
    toast.error(message, { autoClose: timeout });
  } else if (state === "info") {
    // Bonus - also info messages
    toast.info(message, { autoClose: timeout });
  }

  setTimeout(() => {
    // Just waiting, no further action performed
  }, delay);
};

const isPermanentClientError = (status) =>
  typeof status === "number" &&
  status >= 400 &&
  status < 500 &&
  status !== 408 &&
  status !== 429;

// With `honorRetryAfter`, a shed request (503, or any answer naming a
// Retry-After) waits this long before its single retry: the server's hint,
// else the default, at most the cap.
export const SHED_RETRY_DEFAULT_S = 2;
export const SHED_RETRY_MAX_S = 10;

// The server's retry hint in seconds: the Retry-After header, else the JSON
// body's retry_after ({"error":"busy"|"cache_unavailable","retry_after":N}).
// The web does not expose Retry-After to cross-origin pages (no CORS
// Access-Control-Expose-Headers), so in the browser the body is usually the
// only readable hint. null when neither names a number.
const readRetryAfterSeconds = async (response) => {
  const header =
    response.headers && typeof response.headers.get === "function"
      ? response.headers.get("Retry-After")
      : null;
  if (header != null && header !== "" && Number.isFinite(Number(header))) {
    return Number(header);
  }
  try {
    const body = await response.json();
    const seconds = Number(body && body.retry_after);
    return body && body.retry_after != null && Number.isFinite(seconds)
      ? seconds
      : null;
  } catch (error) {
    return null;
  }
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Generic fetch function. Errors for non-2xx responses carry `.status`.
//
// Retry policy: a definitive client error (400/404/...) fails at once; any
// other failure (5xx, 408/429, network, timeout) is retried immediately up to
// `retries` attempts.
//
// The last argument's `honorRetryAfter` (opt-in, used by the body listing)
// changes that for a shed request (503 "busy"/"cache_unavailable", or any
// retryable answer naming a Retry-After): it is retried ONCE, after the
// server's Retry-After (default SHED_RETRY_DEFAULT_S, at most
// SHED_RETRY_MAX_S), and a second shed is surfaced. Sending a heavy listing
// again at once tripled the load on the lane that had just refused it. Its
// `isCurrent` (optional) skips that retry when the page that asked is gone by
// the end of the wait. Callers that do not opt in keep the immediate policy
// for every status.
export const fetchData = async (
  serverUrl,
  endpoint,
  eventName,
  method = "GET",
  bodyData = null,
  retries = 3,
  initialTimeout = 10000,
  { honorRetryAfter = false, isCurrent = () => true } = {}
) => {
  const url = `${serverUrl}${endpoint}?event_name=${eventName}`;
  const options = {
    method,
    headers: {
      "Content-Type": "application/json",
    },
  };

  if (method === "POST" && bodyData) {
    options.body = JSON.stringify(bodyData);
  }

  let shedRetried = false;
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        initialTimeout * attempt
      );

      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (!response.status.toString().startsWith("2")) {
        const httpError = new Error(`HTTP error! Status: ${response.status}`);
        httpError.status = response.status;
        if (response.status === 404) {
          // A 404 may carry a JSON body (check-event: moved_to_site for an
          // event moved to another environment). Best effort only.
          try {
            httpError.data = await response.json();
          } catch {
            httpError.data = null;
          }
        }
        if (honorRetryAfter && !isPermanentClientError(response.status)) {
          const hinted = await readRetryAfterSeconds(response);
          if (response.status === 503 || hinted != null) {
            httpError.retryAfterSeconds = Math.min(
              SHED_RETRY_MAX_S,
              Math.max(1, hinted != null ? hinted : SHED_RETRY_DEFAULT_S)
            );
          }
        }
        throw httpError;
      }

      const data = await response.json();
      return data;
    } catch (error) {
      // A definitive client error (400/404/...) will not change on retry;
      // 408/429 and 5xx/network/timeout errors stay retryable.
      if (isPermanentClientError(error.status)) {
        console.error(`Error fetching ${endpoint} (HTTP ${error.status}):`, error);
        throw error;
      }
      if (error.retryAfterSeconds != null) {
        if (shedRetried || attempt === retries) {
          console.error(
            `Error fetching ${endpoint} (HTTP ${error.status}, shed):`,
            error
          );
          throw error;
        }
        shedRetried = true;
        console.warn(
          `${endpoint} shed (HTTP ${error.status}); retrying once in ${error.retryAfterSeconds} s.`
        );
        await wait(error.retryAfterSeconds * 1000);
        if (!isCurrent()) throw error;
        continue;
      }
      if (attempt === retries) {
        console.error(
          `Error fetching ${endpoint} after ${retries} attempts:`,
          error
        );
        throw error;
      } else if (error.name === "AbortError") {
        console.warn(
          `Timeout on attempt ${attempt} for ${endpoint}. Retrying...`
        );
      } else {
        console.error(`Error on attempt ${attempt} for ${endpoint}:`, error);
      }
    }
  }
};

// Helper function: Converting base64 to Blob safely
export const base64ToBlob = async (base64String, mimeType = "image/webp") => {
  try {
    // Check if the string is valid
    if (!base64String || typeof base64String !== "string") {
      throw new Error("Invalid base64 string");
    }

    // If the string already contains the data: protocol, use it as is
    const dataUrl = base64String.startsWith("data:")
      ? base64String
      : `data:${mimeType};base64,${base64String}`;

    const response = await fetch(dataUrl);
    return await response.blob();
  } catch (error) {
    console.error("Error converting base64 to blob:", error);
    throw error;
  }
};
